import type { VADInputs, VADState, VADStatusReason } from '../types/vad-state';
import { VAD_PRIORITY_ORDER, VAD_STATUS_MAP } from '../const/vad-config';
import { resolveAudioPlaybackVADState } from './vad-audio-playback-policy';

/**
 * VAD 상태 해결 로직
 *
 * 책임:
 * - 다양한 입력 조건을 우선순위에 따라 평가
 * - 최종 VAD 상태 결정
 * - 상태 변경 이유 추적
 */

/**
 * VAD 상태 우선순위 기반 해결
 * 높은 우선순위 조건이 낮은 우선순위를 오버라이드함
 */
export const resolveVADState = (inputs: VADInputs): VADState => {
  const {
    isConsentVisible,
    currentChatMode,
    isMicOn,
    isLoading,
    isSpeaking,
    isAudioPlaying,
  } = inputs;

  // 우선순위 순서대로 조건 검사
  let activeReason: VADStatusReason = 'voice_ready'; // 기본값

  // 1. 동의 모달이 표시됨 (최고 우선순위)
  if (isConsentVisible) {
    activeReason = 'consent_required';
  }
  // 2. 실제 오디오 재생 중 (entrance 애니메이션도 포함)
  else if (resolveAudioPlaybackVADState(isAudioPlaying)) {
    activeReason = 'audio_playing';
  }
  // 3. 아바타 speaking 중 (TTS 재생 중)
  else if (isSpeaking) {
    activeReason = 'speaking';
  }
  // 4. 응답 로딩 중 (메시지 전송 후 응답 대기)
  else if (isLoading) {
    activeReason = 'loading_response';
  }
  // 5. 채팅 모드 활성
  else if (currentChatMode === 'chat') {
    activeReason = 'chat_mode';
  }
  // 6. 마이크 비활성화
  else if (!isMicOn) {
    activeReason = 'mic_disabled';
  }
  // 7. 모든 조건 만족 - 음성 모드 준비 완료
  else {
    activeReason = 'voice_ready';
  }

  const result = {
    status: VAD_STATUS_MAP[activeReason],
    reason: activeReason,
  };

  return result;
};

/**
 * VAD 상태 변경이 필요한지 확인
 */
export const shouldUpdateVADState = (
  currentState: VADState,
  newState: VADState
): boolean => {
  return (
    currentState.status !== newState.status ||
    currentState.reason !== newState.reason
  );
};

/**
 * VAD 상태 디버그 정보 생성
 */
export const createVADDebugInfo = (
  inputs: VADInputs,
  resolvedState: VADState
) => {
  return {
    timestamp: Date.now(),
    inputs: { ...inputs },
    resolved: { ...resolvedState },
    conditions: {
      consentVisible: inputs.isConsentVisible,
      isAudioPlaying: inputs.isAudioPlaying,
      isSpeaking: inputs.isSpeaking,
      isLoading: inputs.isLoading,
      isVoiceMode: inputs.currentChatMode === 'voice',
      micEnabled: inputs.isMicOn,
    },
    priority: VAD_PRIORITY_ORDER.indexOf(resolvedState.reason),
  };
};
